// Mirrors chooseLineCode on the server (services/drawings/matchDrawingCode.ts),
// which decides what the drawing search looks up. Only used to warn early;
// the search itself always decides on the server.

// "20-107579" / "20-107579-1" or a drawing number such as "24107579" / "24107579-1",
// standing alone rather than inside a longer word or number, so "10-15mm"
// or "M8-20" in a name is never taken for a code
const CODE_IN_NAME =
  /(?:^|[^\p{L}\p{N}-])(\d{2}-\d{6}(?:-\d+)?|\d{8}(?:-\d+)?)(?![\p{L}\p{N}-])/gu;

// the default code pattern of the search, and a whole drawing number
const SEARCHABLE_CODE = /^(?:\d{2}-.+|\d{8}(?:-\d+)?)$/;

/** The distinct codes written into an item's name, in order. */
export function codesInName(name: string | null | undefined): string[] {
  const found = new Set<string>();

  if (!name) {
    return [];
  }

  const subject = name.normalize("NFC");
  let match: RegExpExecArray | null;

  CODE_IN_NAME.lastIndex = 0;

  while ((match = CODE_IN_NAME.exec(subject)) !== null) {
    found.add(match[1]);
  }

  return Array.from(found);
}

/** Whether the line gives the drawing search a code, in its name or Item code. */
export function hasDrawingCode(
  name: string | null | undefined,
  code: string | null | undefined,
): boolean {
  return codesInName(name).length > 0 || !!(code && code.trim());
}

/**
 * The codes of a line that names more than one drawing - a code in its name
 * and a different Item code, or several codes in its name - which the
 * drawing search will skip. Null when there is no such conflict.
 */
export function conflictingDrawingCodes(
  name: string | null | undefined,
  code: string | null | undefined,
): string[] | null {
  const named = codesInName(name);
  const itemCode = code ? code.normalize("NFC").trim() : "";

  if (named.length > 1) {
    return named;
  }

  if (
    named.length === 1 &&
    SEARCHABLE_CODE.test(itemCode) &&
    itemCode !== named[0]
  ) {
    return [named[0], itemCode];
  }

  return null;
}

/** The warning shown under an item's inputs for a conflicting line. */
export function drawingCodeConflictMessage(
  name: string | null | undefined,
  code: string | null | undefined,
): string | undefined {
  const codes = conflictingDrawingCodes(name, code);

  if (!codes) {
    return undefined;
  }

  return codesInName(name).length > 1
    ? `The name has more than one code (${codes.join(
        ", ",
      )}). Drawings will not be searched for this item until only one remains.`
    : `The name has code ${codes[0]} but Item code is ${codes[1]}. Drawings will not be searched for this item until they match or one is removed.`;
}
